// Mapa de atajos. Se evitaron teclas que el navegador reserva: Alt+D (barra de direcciones), F10
// (menú), F11 (pantalla completa), F12 (herramientas), Ctrl+W/Ctrl+T/Ctrl+N y Ctrl+1..8 (pestañas del
// navegador), Alt+F/Alt+E (menú). F5, F6 y F7 se usan SOLO dentro de la nota y de la consulta de
// cliente (panel); fuera de ellas quedan para el navegador. Matriz medida en Chrome y Edge:
// docs/frontend/13-matriz-teclado.md.
// Ningún atajo ejecuta algo destructivo sin confirmación: F9 abre el RESUMEN, no confirma.
import { useEffect, useRef } from 'react';

export interface Atajo {
  tecla: string;
  descripcion: string;
  ambito: 'global' | 'nota' | 'grilla' | 'modal';
}

export const ATAJOS: readonly Atajo[] = [
  { tecla: 'F1', descripcion: 'Ayuda de atajos', ambito: 'global' },
  { tecla: 'Ctrl+K', descripcion: 'Buscar cliente sin crear nota (consulta)', ambito: 'global' },
  { tecla: 'Alt+N', descripcion: 'Nueva nota de venta', ambito: 'global' },
  { tecla: 'Alt+O', descripcion: 'Consulta de documentos (nota → DTE)', ambito: 'global' },
  { tecla: 'Alt+1…9', descripcion: 'Ir a la pestaña 1…9', ambito: 'global' },
  {
    tecla: 'Alt+Q',
    descripcion: 'Cerrar la pestaña activa (pide confirmación si hay cambios)',
    ambito: 'global',
  },
  { tecla: 'F2', descripcion: 'Buscar / cambiar cliente', ambito: 'nota' },
  { tecla: 'F3', descripcion: 'Buscar producto y agregar línea', ambito: 'nota' },
  { tecla: 'F4', descripcion: 'Ir a la cantidad de la línea seleccionada', ambito: 'nota' },
  {
    tecla: 'Alt+S',
    descripcion: 'Stock / disponibilidad del producto de la línea',
    ambito: 'nota',
  },
  { tecla: 'Alt+C', descripcion: 'Ficha rápida del cliente', ambito: 'nota' },
  {
    tecla: 'F8',
    descripcion:
      'Recalcular ahora (el backend precia sin guardar; también lo hace solo al cambiar algo)',
    ambito: 'nota',
  },
  {
    tecla: 'F9',
    descripcion: 'Revisar y confirmar (guarda, abre el resumen; confirma con Enter)',
    ambito: 'nota',
  },
  { tecla: 'F5', descripcion: 'Panel: cartola del cliente', ambito: 'nota' },
  { tecla: 'F6', descripcion: 'Panel: pedidos y seguimiento', ambito: 'nota' },
  { tecla: 'F7', descripcion: 'Panel: últimas compras', ambito: 'nota' },
  {
    tecla: 'Alt+M',
    descripcion: 'Otros datos (canal, digitado por, documento, flete, observaciones)',
    ambito: 'nota',
  },
  {
    tecla: 'Alt+P',
    descripcion: 'Ir al despacho de la nota confirmada (solo con permiso de despacho)',
    ambito: 'nota',
  },
  {
    tecla: 'código + Enter',
    descripcion:
      'Código exacto: agrega la línea y va a la cantidad (si no es exacto, abre el buscador)',
    ambito: 'grilla',
  },
  {
    tecla: 'Enter',
    descripcion:
      'Navegando: editar la celda · Editando: confirmar y pasar al siguiente campo editable (cantidad → precio → descuento → código)',
    ambito: 'grilla',
  },
  { tecla: '↑ ↓ ← →', descripcion: 'Moverse por la grilla sin editar nada', ambito: 'grilla' },
  {
    tecla: 'Esc',
    descripcion: 'Salir de la edición sin perder la línea (vuelve a navegar)',
    ambito: 'grilla',
  },
  {
    tecla: '5c',
    descripcion: 'En Cantidad: 5 cajas (RTU); se guarda en unidad base (5 × 9 = 45 UN)',
    ambito: 'grilla',
  },
  {
    tecla: 'Supr',
    descripcion: 'Quitar la línea seleccionada (navegando; pide confirmación)',
    ambito: 'grilla',
  },
  { tecla: 'Esc', descripcion: 'Cerrar el buscador o el diálogo (no borra nada)', ambito: 'modal' },
];

/** Nombre normalizado de una combinación: "Alt+S", "F9", "Ctrl+Delete". */
export function combinacion(e: KeyboardEvent): string {
  const partes: string[] = [];
  if (e.ctrlKey) partes.push('Ctrl');
  if (e.altKey) partes.push('Alt');
  if (e.shiftKey && e.key.length > 1) partes.push('Shift');
  const k = e.key.length === 1 ? e.key.toUpperCase() : e.key;
  partes.push(k);
  return partes.join('+');
}

/**
 * Escucha atajos mientras `activo` (la pestaña visible y sin diálogo encima). El mapa puede cambiar
 * en cada render: se lee por referencia para no re-suscribir.
 */
export function useAtajos(activo: boolean, mapa: Record<string, (e: KeyboardEvent) => void>): void {
  const ref = useRef(mapa);
  ref.current = mapa;
  useEffect(() => {
    if (!activo) return;
    const alPresionar = (e: KeyboardEvent) => {
      const fn = ref.current[combinacion(e)];
      if (!fn) return;
      e.preventDefault();
      e.stopPropagation();
      fn(e);
    };
    window.addEventListener('keydown', alPresionar);
    return () => window.removeEventListener('keydown', alPresionar);
  }, [activo]);
}
